/**
 * 🔴 WHAT A RUN DID, STEP BY STEP (2026-10-08). The owner, on a report whose essential tests read
 * "not run": "we need to check and have more logs, to understand the problems! also the turns if
 * reach maximum turns!". The jail keeps one row per tool call — the command or file it named,
 * whether it ran, was refused or errored, and the tail of a sandbox call's output — and how the
 * run ended against its limits. This draws it, on the report and from the Runs page alike.
 * ⚠️ A refused step is the one that most often explains a test that never ran, so refusals are
 * counted in the summary line and stand out in the table.
 */
import { useEffect, useId, useState } from "react";
import { getRunLog, type ApiError, type RunLog, type RunLogStep } from "./api";
import { Badge, ErrorBox, type Tone } from "./ui";

const STATUS: Record<RunLogStep["status"], { word: string; tone: Tone }> = {
  ok: { word: "ran", tone: "ok" },
  refused: { word: "refused", tone: "warn" },
  error: { word: "error", tone: "stop" },
  no_result: { word: "no answer", tone: "plain" },
};

/** What the CLI's kind of refusal means here. [X] `mode` is what 2.1.288 reports both for a
 *  command that is not allowed and for an allowed one its own check on the text would not pass
 *  (a line inside starting with `#`): either way it wanted a person, and a grading run has none. */
const REASON: Record<string, string> = {
  mode: "Refused: the CLI wanted a person to approve it, and nobody can in a grading run — a command that is not sandbox-run, or one whose text the CLI's own check would not pass.",
};

/** "20 of 60 turns", or "20 turns" when the limit is not known. */
export function turnsOf(turns: number | null | undefined, max: number | null | undefined): string {
  if (turns == null) return "";
  return max != null ? `${turns} of ${max} turns` : `${turns} turns`;
}

export function RunLogView({ log }: { log: RunLog }) {
  if (!log.steps || !log.ended) {
    return <p className="small muted">This run was made before runs kept a log of their steps.</p>;
  }
  const { ended, counts } = log;
  const total = log.steps_total ?? log.steps.length;
  return (
    <div className="rl">
      <p className="rl-sum">
        {ended.at_limit && <><Badge tone="stop">turn limit</Badge>{" "}</>}
        <b className="num">{turnsOf(ended.turns, log.limits?.max_turns) || "turns not recorded"}</b>
        {" — it "}{ended.sentence}.
        {counts && <>{" "}{total} steps: {counts.ok} ran
          {counts.refused > 0 && <>, <b className="rl-refused">{counts.refused} refused</b></>}
          {counts.error > 0 && <>, {counts.error} with an error</>}
          {counts.no_result > 0 && <>, {counts.no_result} with no answer</>}.</>}
        {total > log.steps.length &&
          <span className="muted"> Showing the first {log.steps.length}.</span>}
      </p>
      {/* ⚠️ A LIST, NOT A TABLE: [X] at phone width a five-column table scrolled sideways and put
          the result — the column that answers "why did it not run" — off the screen. */}
      <ol className="rl-steps">
        {log.steps.map((s, i) => (
          <li key={i} className={`rl-step${s.status === "refused" ? " rl-row-refused" : ""}`}>
            <div className="rl-step-head">
              <span className="num muted">{i + 1}</span>
              <b>{s.tool}</b>
              <span className="muted">turn {s.turn ?? "?"}</span>
              {s.chars != null && <span className="muted">{s.chars} characters</span>}
              <span className="spacer" />
              <Badge tone={STATUS[s.status].tone}>{STATUS[s.status].word}</Badge>
            </div>
            <code className="rl-what">{s.what || "—"}</code>
            {s.status === "refused" &&
              <div className="small muted">{REASON[s.reason] ?? `refused by the CLI (${s.reason || "no reason given"})`}</div>}
            {s.output && (
              <details className="rl-out">
                <summary>{s.status === "ok" ? "Output (the end of it)" : "What it said"}</summary>
                <pre className="md-pre">{s.output}</pre>
              </details>)}
          </li>))}
      </ol>
      {(log.scripts?.length ?? 0) > 0 && (
        <div className="rl-scripts">
          <div className="small muted">The scripts it left in ./sandbox, as they were when the
            run ended — what it sent to the sandbox.</div>
          {log.scripts!.map((sc) => (
            <details key={sc.name} className="rl-script">
              <summary><code>{sc.name}</code>
                {sc.chars != null && <span className="muted"> · {sc.chars} characters</span>}</summary>
              <pre className="md-pre">{sc.content}</pre>
            </details>))}
        </div>)}
    </div>);
}

/** The Runs page's way in: a run that stored nothing has no report to carry its log. */
export function RunLogDialog({ courseId, attemptId, onClose }: {
  courseId: string; attemptId: string; onClose: () => void;
}) {
  const ids = useId();
  const [log, setLog] = useState<{ outcome: string; error: string; run_log: RunLog } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  useEffect(() => {
    let live = true;
    getRunLog(courseId, attemptId).then((r) => { if (live) setLog(r); })
      .catch((e) => { if (live) setErr(e as ApiError); });
    return () => { live = false; };
  }, [courseId, attemptId]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal wide rl-modal" role="dialog" aria-modal="true" aria-labelledby={`${ids}-h`}>
        <div className="modal-head">
          <strong id={`${ids}-h`}>Run log</strong>
          <span className="spacer" />
          <button className="btn ghost sm" type="button" aria-label="Close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err ? <ErrorBox err={err} />
            : !log ? <p className="small muted">Reading the log…</p>
            : <>{log.error && <p className="small rl-error">{log.error}</p>}
                <RunLogView log={log.run_log} /></>}
        </div>
      </div>
    </div>);
}
